import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { messagesApi } from '../api';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { Loader2, MessageSquare, Send, Users, UserPlus, X } from 'lucide-react';

function timeAgo(iso) {
  if (!iso) return '';
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'teraz';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h`;
  const d = Math.floor(h / 24);
  return `${d} d`;
}

/**
 * Modal tworzenia czatu grupowego. Uczestnicy wybierani z rozmów 1:1 usera
 * (min. 2 — grupa wymaga >=3 osób łącznie z twórcą).
 */
function NewGroupModal({ partners, onClose, onCreate }) {
  const [name, setName] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || selected.size < 2 || saving) return;
    setSaving(true);
    try {
      await onCreate(name.trim(), [...selected]);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl max-w-md w-full my-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Users className="w-5 h-5 text-slate-500" /> Nowa grupa
          </h2>
          <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Nazwa grupy</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              maxLength={100}
              placeholder="np. Zespół projektowy"
              className="w-full px-3 py-2 border border-slate-200 rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Uczestnicy (min. 2) — z Twoich rozmów 1:1
            </label>
            {partners.length === 0 ? (
              <p className="text-xs text-slate-400 py-3">
                Brak rozmów 1:1. Najpierw napisz do kilku osób, potem złożysz z nich grupę.
              </p>
            ) : (
              <div className="max-h-60 overflow-y-auto border border-slate-200 rounded divide-y divide-slate-100">
                {partners.map(u => (
                  <label key={u.id} className="flex items-center gap-2 p-2 hover:bg-slate-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selected.has(u.id)}
                      onChange={() => toggle(u.id)}
                      className="w-4 h-4"
                    />
                    <img
                      src={u.avatar || `https://ui-avatars.com/api/?name=${u.name || '?'}&background=10b981&color=fff`}
                      alt=""
                      className="w-7 h-7 rounded-full"
                    />
                    <span className="text-sm text-slate-800">{u.name || u.email}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-slate-200">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded">Anuluj</button>
          <button
            type="submit"
            disabled={!name.trim() || selected.size < 2 || saving}
            className="flex items-center gap-1 px-4 py-2 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Utwórz grupę
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Wiadomości — DM 1:1 oraz czaty grupowe. Lista konwersacji + chat view obok.
 * Query: ?conv=<id> otwiera konkretną, ?with=<user_id> tworzy/otwiera DM z X.
 */
export default function MessagesPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [senders, setSenders] = useState({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [input, setInput] = useState('');
  const [showNewGroup, setShowNewGroup] = useState(false);
  const scrollRef = useRef(null);

  const scrollDown = (delay = 50) => setTimeout(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, delay);

  const loadConversations = async () => {
    try {
      const r = await messagesApi.listConversations();
      setConversations(r.data?.items || []);
      return r.data?.items || [];
    } catch (e) { console.error(e); return []; }
  };

  const openConversation = async (convId) => {
    try {
      const r = await messagesApi.listMessages(convId);
      setMessages(r.data?.items || []);
      setSenders(r.data?.senders || {});
      setActiveConv(r.data?.conversation || conversations.find(c => c.id === convId));
      setSearchParams(prev => {
        const p = new URLSearchParams(prev);
        p.set('conv', convId);
        return p;
      });
      scrollDown(100);
    } catch (e) { console.error(e); }
  };

  const startWith = async (targetUserId) => {
    try {
      const r = await messagesApi.createConversation(targetUserId);
      await loadConversations();
      await openConversation(r.data?.id);
    } catch (e) { alert('Nie udało się otworzyć rozmowy: ' + (e?.response?.data?.detail || e.message)); }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const convs = await loadConversations();
      const convId = searchParams.get('conv');
      const withUserId = searchParams.get('with');
      if (convId) await openConversation(convId);
      else if (withUserId) await startWith(withUserId);
      else if (convs.length > 0) await openConversation(convs[0].id);
      setLoading(false);
    })();
  }, []);

  // Polling — refresh aktualnej konwersacji + listy co 15s (lekka wersja, bez WebSocket)
  useEffect(() => {
    if (!activeConv?.id) return;
    const id = setInterval(async () => {
      try {
        const r = await messagesApi.listMessages(activeConv.id);
        const items = r.data?.items || [];
        if (items.length !== messages.length) {
          setMessages(items);
          setSenders(r.data?.senders || {});
          scrollDown(50);
        }
        loadConversations();
      } catch {}
    }, 15000);
    return () => clearInterval(id);
  }, [activeConv?.id, messages.length]);

  const submit = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending || !activeConv?.id) return;
    setSending(true);
    setInput('');
    try {
      await messagesApi.sendMessage(activeConv.id, text);
      const r = await messagesApi.listMessages(activeConv.id);
      setMessages(r.data?.items || []);
      setSenders(r.data?.senders || {});
      await loadConversations();
      scrollDown(50);
    } catch (e) { alert('Wyślij: ' + (e?.response?.data?.detail || e.message)); }
    finally { setSending(false); }
  };

  const handleCreateGroup = async (name, memberIds) => {
    try {
      const r = await messagesApi.createGroup(name, memberIds);
      setShowNewGroup(false);
      await loadConversations();
      await openConversation(r.data?.id);
    } catch (e) { alert('Grupa: ' + (e?.response?.data?.detail || e.message)); }
  };

  const leaveGroup = async () => {
    if (!activeConv?.id || !activeConv.is_group) return;
    if (!window.confirm(`Opuścić grupę "${activeConv.name || ''}"?`)) return;
    try {
      await messagesApi.leaveConversation(activeConv.id);
      setActiveConv(null);
      setMessages([]);
      await loadConversations();
    } catch (e) { alert('Nie udało się opuścić grupy: ' + (e?.response?.data?.detail || e.message)); }
  };

  // Partnerzy z rozmów 1:1 — źródło uczestników nowej grupy
  const dmPartners = conversations
    .filter(c => !c.is_group && c.other_user)
    .map(c => c.other_user);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <MessageSquare className="w-6 h-6 text-indigo-500" /> Wiadomości
        </h1>
        <button
          onClick={() => setShowNewGroup(true)}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
        >
          <UserPlus className="w-4 h-4" /> Nowa grupa
        </button>
      </div>

      {showNewGroup && (
        <NewGroupModal
          partners={dmPartners}
          onClose={() => setShowNewGroup(false)}
          onCreate={handleCreateGroup}
        />
      )}

      <div className="grid lg:grid-cols-[300px_1fr] gap-4 h-[calc(100vh-12rem)]">
        {/* Lista konwersacji */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-30" />
              Brak rozmów. Otwórz profil osoby lub zaakceptowane połączenie żeby napisać,
              albo utwórz grupę.
            </div>
          ) : (
            conversations.map(c => {
              const isActive = activeConv?.id === c.id;
              const isGroup = c.is_group;
              const title = isGroup
                ? (c.name || 'Grupa')
                : (c.other_user?.name || c.other_user?.email || 'Użytkownik');
              return (
                <button
                  key={c.id}
                  onClick={() => openConversation(c.id)}
                  className={`w-full text-left p-3 border-b border-slate-100 hover:bg-slate-50 flex items-start gap-3 ${isActive ? 'bg-indigo-50' : ''}`}
                >
                  {isGroup ? (
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <Users className="w-5 h-5 text-indigo-600" />
                    </div>
                  ) : (
                    <img
                      src={c.other_user?.avatar || `https://ui-avatars.com/api/?name=${c.other_user?.name || '?'}&background=10b981&color=fff`}
                      alt=""
                      className="w-10 h-10 rounded-full flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1">
                      <span className={`text-sm truncate ${c.is_unread ? 'font-bold text-slate-900' : 'font-medium text-slate-700'}`}>
                        {title}
                      </span>
                      <span className="text-[10px] text-slate-400">{timeAgo(c.last_message_at)}</span>
                    </div>
                    <p className={`text-xs truncate mt-0.5 ${c.is_unread ? 'text-slate-700 font-medium' : 'text-slate-500'}`}>
                      {isGroup && <span className="text-slate-400">{(c.members?.length || 0) + 1} os. · </span>}
                      {c.last_message_preview || 'Brak wiadomości'}
                    </p>
                  </div>
                  {c.is_unread && <span className="w-2 h-2 rounded-full bg-indigo-500 mt-2 flex-shrink-0" />}
                </button>
              );
            })
          )}
        </div>

        {/* Chat view */}
        <div className="bg-white rounded-xl border border-slate-200 flex flex-col">
          {activeConv ? (
            <>
              <div className="p-3 border-b border-slate-100 flex items-center gap-3">
                {activeConv.is_group ? (
                  <>
                    <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <Users className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">{activeConv.name || 'Grupa'}</p>
                      <p className="text-[11px] text-slate-500">
                        {activeConv.participant_ids?.length ?? ((activeConv.members?.length || 0) + 1)} uczestników
                      </p>
                    </div>
                    <button
                      onClick={leaveGroup}
                      className="text-xs text-rose-600 hover:text-rose-700 border border-rose-200 rounded px-2 py-1 flex-shrink-0"
                    >
                      Opuść grupę
                    </button>
                  </>
                ) : (
                  <>
                    <img
                      src={activeConv.other_user?.avatar || `https://ui-avatars.com/api/?name=${activeConv.other_user?.name || '?'}&background=10b981&color=fff`}
                      alt=""
                      className="w-9 h-9 rounded-full"
                    />
                    <div>
                      <p className="font-medium text-slate-900">{activeConv.other_user?.name || activeConv.other_user?.email || 'Użytkownik'}</p>
                      <p className="text-[11px] text-slate-500">{activeConv.other_user?.email}</p>
                    </div>
                  </>
                )}
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2">
                {messages.length === 0 ? (
                  <div className="text-center text-sm text-slate-400 py-8">Brak wiadomości. Napisz pierwszą.</div>
                ) : (
                  messages.map(m => {
                    const isMine = m.sender_id === user?.id;
                    const showSender = activeConv?.is_group && !isMine;
                    return (
                      <div key={m.id} className={`flex flex-col ${isMine ? 'items-end' : 'items-start'}`}>
                        {showSender && (
                          <span className="text-[10px] text-slate-500 mb-0.5 ml-1">
                            {senders[m.sender_id]?.name || 'Użytkownik'}
                          </span>
                        )}
                        <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                          isMine
                            ? 'bg-indigo-600 text-white rounded-br-sm'
                            : 'bg-slate-100 text-slate-900 rounded-bl-sm'
                        }`}>
                          {m.content}
                          <div className={`text-[10px] mt-1 ${isMine ? 'text-indigo-100' : 'text-slate-400'}`}>
                            {new Date(m.created_at).toLocaleString('pl-PL', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <form onSubmit={submit} className="p-3 border-t border-slate-100 flex gap-2">
                <input
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder="Napisz wiadomość..."
                  disabled={sending}
                  className="flex-1 px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || sending}
                  className="px-3 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>
            </>
          ) : (
            <div className="flex items-center justify-center flex-1 text-sm text-slate-400">
              Wybierz rozmowę z lewej.
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
